import { useEffect, useMemo, useRef, useState } from 'react';
import HoleMap, { type MapControls } from '../components/HoleMap';
import type { Course, FleetPosition, Hole, LngLat, Weather } from '../lib/types';
import type { Fix } from '../lib/gps';
import { bearingDeg, distanceM, formatDistance, insidePolygon, playingDistance } from '../lib/geo';
import { holeCorridorRing } from '../lib/shapes';
import { buildGraph, routeAlongPaths } from '../lib/routing.ts';
import { listen, speechRecognitionSupported, type Listener } from '../lib/speech.ts';
import {
  soundCalculating, soundConfirm, soundListenCancel, soundListenStart, soundReject, unlockSounds,
} from '../lib/sounds.ts';
import { useDeviceHeading, resolveHeading } from '../lib/heading.ts';
import {
  decide as decideVoice,
  initialVoiceState,
  speak,
  stopSpeaking,
  type VoiceState,
} from '../lib/voice.ts';
import { clearYardage, pushYardage } from '../lib/yardageWidget.ts';
import HoleTurnBanner from '../components/HoleTurnBanner';
import { decideHoleTurn, initialHoleTurnState, type HoleTurn } from '../lib/holeTurn';
import { registerCarPlayCommands } from '../lib/carplayBridge';
import { WEATHER_TEXT } from '../lib/weather';
import { parkedInfo, relativeDirection, type Parked } from '../lib/parked.ts';
import type { FeatureCollection } from 'geojson';

interface Props {
  course: Course;
  /** null until a course is chosen: map and search only, no hole overlay. */
  hole: Hole | null;
  holeIndex: number;
  onHole: (i: number) => void;
  teeColour: string;
  unit: 'm' | 'yd';
  fix: Fix | null;
  weather: Weather | null;
  fleet: FleetPosition[];
  showFleet: boolean;
  breach: { label: string; severity: 'block' | 'warn' } | null;
  cartPaths: FeatureCollection | null;
  lockToHole: boolean;
  voice: boolean;
  /** A spoken command that parsed. The shell decides what to do with it. */
  onSpokenCommand: (transcript: string) => Promise<string | null>;
  parked: Parked | null;
  /** Road / satellite / hybrid / poly on the one map. */
  mapMode: import('../lib/basemap').MapMode;
  /**
   * When supplied, Settings owns the cart-path network instead of the on-map
   * tool button. Left undefined by /map so that surface keeps its own toggle.
   */
  cartPathMode?: boolean;
  /** What the map may draw. */
  indicators?: import('../lib/indicators').Indicators;
  /** 0-1 from the hole detector; gates the golf turn-by-turn banner. */
  confidence?: number;
}

export default function MapScreen(props: Props) {
  const { course, hole, holeIndex, onHole, teeColour, unit, fix, weather, fleet, showFleet, breach,
    cartPaths, parked,
    lockToHole, voice, mapMode, cartPathMode, indicators, confidence = 1,
    onSpokenCommand } = props;
  const [aim, setAim] = useState<LngLat | null>(null);
  const [playerUp, setPlayerUp] = useState(true);
  const [bearing, setBearing] = useState(0);
  const [controls, setControls] = useState<MapControls | null>(null);
  const [offCorridor, setOffCorridor] = useState(false);
  // On by default: opening the app and immediately seeing yourself move is the
  // whole point. It yields the moment the golfer drags.
  const [follow, setFollow] = useState(true);
  const voiceState = useRef<VoiceState>(initialVoiceState());

  // --- microphone ---------------------------------------------------------
  // The search sheet is owned by the shell, not by this screen. It used to live
  // here, which meant it did not exist on any screen that was not the map — so
  // when a course failed to load, the "pick another course" button and the top
  // bar's search both dispatched an event nothing was listening for, and the
  // app was genuinely stuck with no way out.
  const openSearch = () => window.dispatchEvent(new Event('fairway:open-search'));
  // Overlay tools. Numbered rather than iconified: three unlabelled golf
  // pictograms are indistinguishable at a glance on a bumpy buggy, and the
  // numbers stay meaningful as more tools are added.
  /* Golf turn-by-turn. The decision is in lib/holeTurn.ts so it can be tested
     by replay; this only holds the state and renders the result. */
  const turnState = useRef(initialHoleTurnState());
  const [turn, setTurn] = useState<HoleTurn | null>(null);

  useEffect(() => {
    if (!hole) return;
    const r = decideHoleTurn(turnState.current, {
      hole: hole.number,
      par: hole.par,
      length: hole.yardage[teeColour] ?? hole.yardage.White ?? 0,
      unit,
      confidence,
    });
    turnState.current = r.state;
    if (r.turn) setTurn(r.turn);
  }, [hole, teeColour, unit, confidence]);

  /* CarPlay drives the map through buttons only — no touch reaches the base
     view, and cars like a Mercedes have no map touchscreen at all. Same
     handlers the on-screen arrows use, so the two can never drift apart. */
  useEffect(() => {
    if (!controls) return;
    return registerCarPlayCommands({
      holeNext: () => onHole(Math.min(holeIndex + 1, course.holes.length - 1)),
      holePrev: () => onHole(Math.max(holeIndex - 1, 0)),
      zoomIn: controls.zoomIn,
      zoomOut: controls.zoomOut,
      pan: (dx, dy) => controls.panBy(dx, dy),
      panBegin: () => setFollow(false),
      recentre: () => setFollow(true),
    });
  }, [controls, holeIndex, course.holes.length, onHole]);

  const [tool1Aim, setTool1Aim] = useState(false);
  const [tool2Hazards, setTool2Hazards] = useState(true);
  const [tool3Paths, setTool3Paths] = useState(true);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [micNote, setMicNote] = useState<string | null>(null);
  const listener = useRef<Listener | null>(null);

  const runCommand = async (said: string) => {
    setTranscript(said);
    soundCalculating();
    const note = await onSpokenCommand(said);
    if (note) {
      soundConfirm();
      setMicNote(note);
    } else {
      soundReject();
      setMicNote(`Didn't understand "${said}"`);
    }
    window.setTimeout(() => {
      setMicNote(null);
      setTranscript('');
    }, 3200);
  };

  const toggleMic = () => {
    unlockSounds();
    if (listening) {
      listener.current?.stop();
      listener.current = null;
      setListening(false);
      soundListenCancel();
      return;
    }
    setTranscript('');
    setMicNote(null);
    soundListenStart();
    setListening(true);
    listener.current = listen({
      onInterim: setTranscript,
      onFinal: (text) => void runCommand(text),
      onError: (msg) => {
        setMicNote(msg);
        window.setTimeout(() => setMicNote(null), 3200);
      },
      onEnd: () => {
        setListening(false);
        listener.current = null;
      },
    });
    if (!listener.current) setListening(false);
  };
  const { heading: compassHeading, permission: compassPermission, request: askCompass } =
    useDeviceHeading();
  const [shownHeading, setShownHeading] = useState<number | null>(null);

  // Resolve once per fix and hold it, so the buggy never spins to zero when
  // both sources go quiet between updates.
  useEffect(() => {
    setShownHeading((prev) =>
      resolveHeading({
        gpsHeading: fix?.heading ?? null,
        speed: fix?.speed ?? 0,
        compassHeading,
        previous: prev,
      })
    );
  }, [fix, compassHeading]);

  // The aim marker belongs to one hole. Without a Clear button it must never
  // be able to strand itself: it resets on hole change, and tapping it again
  // removes it.
  useEffect(() => {
    setAim(null);
  }, [hole?.number]);

  useEffect(() => {
    if (!tool1Aim) setAim(null);
  }, [tool1Aim]);

  // Hide the rejection hint on a timer. Deriving it from a timestamp at render
  // time would leave it on screen until some unrelated re-render happened.
  useEffect(() => {
    if (!offCorridor) return;
    const id = window.setTimeout(() => setOffCorridor(false), 2200);
    return () => window.clearTimeout(id);
  }, [offCorridor]);

  // --- route to the next tee ----------------------------------------------
  // The graph is rebuilt only when the paths change, not on every GPS fix.
  const graph = useMemo(() => buildGraph(cartPaths), [cartPaths]);

  const nextTee = useMemo(() => {
    const next = course.holes[holeIndex + 1];
    if (!next) return null;
    return next.tees[teeColour] ?? next.tees.White ?? next.green.middle;
  }, [course.holes, holeIndex, teeColour]);

  const routing = useMemo(() => {
    if (!fix || !nextTee) return null;
    return routeAlongPaths(graph, fix.pos, nextTee);
  }, [graph, fix, nextTee]);

  // Same polygon the map draws, so what looks tappable is tappable.
  const corridor = useMemo(
    () => (hole ? holeCorridorRing(hole, teeColour) : null),
    [hole, teeColour]
  );

  const handleAim = (p: LngLat | null) => {
    if (!tool1Aim || !corridor) return;
    if (!p) return setAim(null);
    // Tapping the marker again clears it — there is no Clear button.
    if (aim && distanceM(aim, p) < 15) return setAim(null);
    // Aiming is only meaningful on the hole being played. A tap on the next
    // fairway or someone's back garden is a mis-tap, not an intention.
    if (!insidePolygon(p, corridor)) {
      setOffCorridor(true);
      return;
    }
    setAim(p);
  };

  const from: LngLat | null = fix?.pos ?? (hole ? hole.tees[teeColour] ?? hole.tees.White : null);

  const d = useMemo(() => {
    if (!hole || !from) return null;
    const front = distanceM(from, hole.green.front);
    const middle = distanceM(from, hole.green.middle);
    const back = distanceM(from, hole.green.back);
    const shotBearing = bearingDeg(from, hole.green.middle);
    const rise =
      hole.greenElevation != null && hole.teeElevation != null
        ? hole.greenElevation - hole.teeElevation
        : 0;
    const play = playingDistance({
      flatM: middle,
      riseM: rise,
      windSpeed: weather?.windSpeed,
      windDirFrom: weather?.windDir,
      shotBearing,
    });
    return { front, middle, back, play, shotBearing, rise };
  }, [from, hole, weather]);

  // Voice. The policy lives in lib/voice.ts; this only feeds it and speaks the
  // result, so what gets said is testable without a browser.
  useEffect(() => {
    if (!voice || !fix || !hole || !d) return;
    const { state, say } = decideVoice(voiceState.current, {
      hole: hole.number,
      par: hole.par,
      holeLength: hole.yardage[teeColour] ?? hole.yardage.White ?? 0,
      distanceToGreen: unit === 'yd' ? d.middle / 0.9144 : d.middle,
      unit,
      speed: fix.speed,
      breach: breach?.severity === 'block' ? breach.label : null,
      now: fix.ts,
      accuracy: fix.accuracy,
    });
    voiceState.current = state;
    if (say) speak(say);
  }, [voice, fix, hole, teeColour, unit, d, breach]);

  // Muting must silence what is already mid-sentence, not just what comes next.
  useEffect(() => {
    if (!voice) stopSpeaking();
  }, [voice]);

  // Mirror the hero number onto the home screen and CarPlay dashboard. Driven
  // from here rather than from the shell because this is where the distance is
  // already computed — a second implementation would be a second thing to get
  // wrong. The plugin de-duplicates, so this fires far less than it looks.
  useEffect(() => {
    if (!hole || !d) {
      void clearYardage();
      return;
    }
    const tee = hole.tees[teeColour] ?? hole.tees.White;
    void pushYardage({
      course: course.name,
      hole: hole.number,
      par: hole.par,
      metres: d.play.metres,
      unit,
      // Only send a position when there is a real fix. The widget treats a
      // missing player as "no fix" and frames on the tee instead; sending the
      // tee as the player would draw a golfer standing somewhere they are not.
      ...(fix ? { playerLat: fix.pos[1], playerLng: fix.pos[0] } : {}),
      teeLat: tee[1],
      teeLng: tee[0],
      greenLat: hole.green.middle[1],
      greenLng: hole.green.middle[0],
    });
  }, [course.name, hole, d, unit, teeColour, fix]);

  const aimInfo = useMemo(() => {
    if (!aim || !hole || !from) return null;
    return {
      carry: distanceM(from, aim),
      remaining: distanceM(aim, hole.green.middle),
    };
  }, [aim, from, hole]);

  const adjusted = !!d && Math.abs(d.play.metres - d.middle) >= 1.5;

  return (
    <div className="mapwrap">
      <HoleMap
        lockToHole={lockToHole}
        cartPaths={cartPaths}
        parked={parked ? parked.pos : null}
        course={course}
        hole={hole}
        teeColour={teeColour}
        position={fix?.pos ?? null}
        heading={shownHeading ?? fix?.heading ?? 0}
        aim={aim}
        onAimChange={handleAim}
        onBearing={setBearing}
        onControls={setControls}
        route={(cartPathMode ?? tool3Paths) && routing?.ok ? routing.path : null}
        showHazards={tool2Hazards}
        showCartPaths={cartPathMode ?? tool3Paths}
        follow={follow}
        onFollowBreak={() => setFollow(false)}
        fleet={fleet}
        showFleet={showFleet}
        playerUp={playerUp}
        mapMode={mapMode}
        indicators={indicators}
      />

      <HoleTurnBanner turn={turn} onDone={() => setTurn(null)} />

      {/* Hole banner. Top centre, biggest non-yardage element on the screen —
          a driver glancing up should know which hole they are on without
          reading anything else. Hidden while a keep-off warning is showing,
          because that warning owns this space and matters more. */}
      {!breach && !turn && hole && (
        <div className="holebanner">
          HOLE {hole.number}
        </div>
      )}

      {/* No course chosen. The map still shows where the golfer is — that part
          is always true — but every hole overlay is withheld rather than
          guessed at, and the one thing to do next is named. */}
      {!hole && (
        <button className="pickcourse" onClick={openSearch}>
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
            <path d="M15.4 15.4 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          <span>
            <strong>Find your course</strong>
            <em>1,449 Australian courses</em>
          </span>
        </button>
      )}

      {offCorridor && (
        <div className="aimhint">Aim points only work on this hole</div>
      )}


      <div className="tools" hidden={!hole}>
        <button
          className={`tool ${tool1Aim ? 'on' : ''}`}
          onClick={() => setTool1Aim((v) => !v)}
          title="Aim finder — tap the map for carry distance"
        >
          1
        </button>
        <button
          className={`tool ${tool2Hazards ? 'on' : ''}`}
          onClick={() => setTool2Hazards((v) => !v)}
          title="Hazards — bunkers and water"
        >
          2
        </button>
        <button
          className={`tool ${tool3Paths ? 'on' : ''}`}
          onClick={() => setTool3Paths((v) => !v)}
          title="Cart paths and the route to the next tee"
        >
          3
        </button>
      </div>

      {speechRecognitionSupported() && (
        <button
          className={`mic ${listening ? 'live' : ''}`}
          onClick={toggleMic}
          aria-label={listening ? 'Stop listening' : 'Speak a command'}
        >
          <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
            <path
              d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
              fill="currentColor"
            />
            <path
              d="M18 12a6 6 0 0 1-12 0M12 18v3"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      {hole && tool3Paths && routing && !aim && (
        <div className={`routebar ${routing.ok ? '' : 'muted-route'}`}>
          {routing.ok ? (
            <>
              <strong>{formatDistance(routing.distanceM, unit)}{unit}</strong> to the next tee
              along the path
            </>
          ) : routing.reason === 'no-paths' ? (
            'No cart paths mapped for this course yet'
          ) : routing.reason === 'too-far-from-path' ? (
            'Too far from a cart path to route'
          ) : (
            'No connected path to the next tee'
          )}
        </div>
      )}

      {(transcript || micNote) && (
        <div className={`heard ${micNote ? 'settled' : ''}`}>
          {micNote ?? `“${transcript}”`}
        </div>
      )}

      {!follow && (
        <button className="recentre" onClick={() => setFollow(true)} disabled={!fix}>
          ◎ Recentre
        </button>
      )}

      {/* Pan and zoom, on the edges. A resistive head-unit touch screen handles
          a tap far better than a drag, and a golfer with wet or gloved hands
          cannot pinch reliably — so every map movement has a button. */}
      {controls && (
        <>
          <button className="pan pan-up" onClick={() => controls.panBy(0, -1)} aria-label="Pan up">▲</button>
          <button className="pan pan-down" onClick={() => controls.panBy(0, 1)} aria-label="Pan down">▼</button>
          <button className="pan pan-left" onClick={() => controls.panBy(-1, 0)} aria-label="Pan left">◀</button>
          <button className="pan pan-right" onClick={() => controls.panBy(1, 0)} aria-label="Pan right">▶</button>
          <div className="zoomstack">
            <button className="pan" onClick={controls.zoomIn} aria-label="Zoom in">+</button>
            <button className="pan" onClick={controls.zoomOut} aria-label="Zoom out">−</button>
          </div>
        </>
      )}

      {compassPermission === 'prompt' && (
        <button className="compass-ask" onClick={() => void askCompass()}>
          Enable compass
        </button>
      )}

      {/* Compass. The map rotates to put the hole up the screen, which is the
          right default but leaves no clue which way is actually north. Tapping
          it flips between player-up and north-up, same as Waze. */}
      <button
        className={`compass ${playerUp ? 'player-up' : 'north-up'}`}
        onClick={() => setPlayerUp((v) => !v)}
        title={playerUp ? 'Player up — tap for north up' : 'North up — tap for player up'}
        aria-label="Toggle map orientation"
      >
        <svg viewBox="0 0 48 48" width="48" height="48" style={{ transform: `rotate(${-bearing}deg)` }}>
          <circle cx="24" cy="24" r="21" fill="rgba(11,18,13,.86)" stroke="#2a3a30" strokeWidth="2" />
          <polygon points="24,7 29,25 24,21 19,25" fill="#ff4d4d" />
          <polygon points="24,41 19,23 24,27 29,23" fill="#eaf3ec" opacity=".55" />
        </svg>
        <span className="c-n">{playerUp ? 'N' : 'N'}</span>
        <span className="c-mode">{playerUp ? 'PLAY' : 'NORTH'}</span>
      </button>

      {/* Par, left edge, vertically centred. Deliberately away from the
          yardage so the two never get misread for each other. */}
      {hole && (
        <div className="parplate">
          <span className="pp-label">PAR</span>
          <span className="pp-value">{hole.par}</span>
        </div>
      )}

      {breach && (
        <div className={`alert ${breach.severity === 'warn' ? 'warn' : ''}`}>
          {breach.severity === 'block' ? 'KEEP OFF' : 'CAUTION'} — {breach.label}
        </div>
      )}

      {hole && d && (
      <div className="hero">
        <div className="label">{aimInfo ? 'AIM POINT' : 'TO CENTRE'}</div>
        <div className="big">
          {formatDistance(aimInfo ? aimInfo.carry : d.play.metres, unit)}
          <span className="unit">{unit}</span>
        </div>
        <div className="sub">
          {aimInfo ? (
            <>then {formatDistance(aimInfo.remaining, unit)} {unit} in · tap map to move</>
          ) : adjusted ? (
            <>
              plays {formatDistance(d.play.metres - d.middle, unit) > 0 ? '+' : ''}
              {formatDistance(d.play.metres - d.middle, unit)} {unit} · flat{' '}
              {formatDistance(d.middle, unit)}
            </>
          ) : (
            <>flat {formatDistance(d.middle, unit)} {unit}</>
          )}
        </div>
      </div>
      )}

      {hole && d && (
      <div className="fmb">
        <div className="row">
          <span>BACK</span>
          <span>{formatDistance(d.back, unit)}</span>
        </div>
        {/* The middle number is the one you club off — it carries the gold
            highlight, same visual grammar as every rangefinder card. */}
        <div className="row mid">
          <span>MIDDLE</span>
          <span>{formatDistance(d.middle, unit)}</span>
        </div>
        <div className="row">
          <span>FRONT</span>
          <span>{formatDistance(d.front, unit)}</span>
        </div>
        {weather && (
          <div className="row" style={{ borderTop: '1px solid var(--line)', paddingTop: 6 }}>
            <span>WIND</span>
            <span>{weather.windSpeed.toFixed(0)} m/s</span>
          </div>
        )}
        {weather && (
          <div className="muted" style={{ textAlign: 'right' }}>
            {Math.round(weather.tempC)}°C · {WEATHER_TEXT[weather.code] ?? '—'}
          </div>
        )}
      </div>
      )}

      {parked && hole && (() => {
        const pi = parkedInfo(parked, fix?.pos ?? null, hole.green.middle);
        return (
          <div className="parked">
            <div className="label">BUGGY PARKED</div>
            <div className="pk-main">
              {formatDistance(pi.buggyToGreen, unit)}
              <span className="pk-unit">{unit} to green</span>
            </div>
            <div className="pk-sub">
              {pi.fromMe > 8
                ? `${formatDistance(pi.fromMe, unit)}${unit} back · ${relativeDirection(pi.bearingToBuggy, fix?.heading ?? null)}`
                : 'you are at the buggy'}
            </div>
          </div>
        );
      })()}

      {/* Hole stepper, bottom centre. The old bar carried par, stroke index
          and three toggles that all now live elsewhere — orientation moved onto
          the compass, parking and hole-lock onto the menu. What is left is the
          one thing with nowhere else to be. */}
      <div className="holestep" hidden={!hole}>
        <button
          className="step"
          onClick={() => onHole(holeIndex - 1)}
          disabled={holeIndex === 0}
          aria-label="Previous hole"
        >
          ‹
        </button>
        <span className="step-meta">
          {hole ? `${hole.yardage[teeColour] ?? hole.yardage.White}` : ''}
          {unit === 'yd' ? 'yd' : 'm'} · {teeColour}
        </span>
        <button
          className="step"
          onClick={() => onHole(holeIndex + 1)}
          disabled={holeIndex >= course.holes.length - 1}
          aria-label="Next hole"
        >
          ›
        </button>
      </div>
    </div>
  );
}
